import z from 'zod';
import {
  decomActions,
  isVaultExportAction,
} from '../../../../shared/decom-actions';
import { destUserRequiredActions } from './decom-policy-utils';
import { VAULT_CORPUS_VALUES } from '../../../../../types';
export const DestUserSchema = z.object({
  userEmail: z.string(),
  role: z.enum(['reader', 'commenter', 'writer', 'owner']),
  type: z.enum(['user', 'group']).catch('user'),
});

// DecomActionModel Schema
export const DecomActionSchema = z
  .object({
    actionType: z.string(),
    destUser: z.array(DestUserSchema).nullable(),
    newOrgPath: z.string().nullable(),
    notifyUser: z.boolean().catch(false),
    userToNotify: z.array(z.string()).catch([]),

    startdate: z.number(),
    starttime: z.string().nullable(),
    enddate: z.number(),
    endtime: z.string().nullable(),
    getDeleted: z.boolean().nullable(),
    query: z.string().nullable(),
    exportType: z.enum(['FULL', 'HEADERS']).nullable(),
    matterId: z.string().nullable(),
    createMatterOnDecom: z.boolean().nullable().catch(false),
    matterNameTemplateCustomText: z.string().nullable().catch(''),
    matterNameTemplateProfileField: z.string().nullable().catch(''),
    matterNameTemplateDescription: z.string().nullable().catch(''),
    matterRegion: z.string().nullable().catch('ANY'),
    corpus: z.enum(VAULT_CORPUS_VALUES).nullable(),
    dataScope: z
      .enum(['ALL_DATA', 'HELD_DATA', 'UNPROCESSED_DATA', 'UNKNOWN'])
      .nullable(),
    searchMethod: z
      .enum([
        'ACCOUNT',
        'ORG_UNIT',
        'TEAM_DRIVE',
        'SHARED_DRIVE',
        'SITES_URL',
        'ROOM',
        'UNKNOWN',
      ])
      .nullable(),
    timeZone: z.string().nullable(),
    terms: z.string().nullable(),
    exportFormat: z.enum(['MBOX', 'PST', 'ICS', 'XML', 'UNKNOWN']).nullable(),
    vaultStartTime: z.string().nullable(),
    vaultEndTime: z.string().nullable(),
    vaultVersionDate: z.string().nullable().catch(null),
    vaultExcludeDrafts: z.boolean().nullable().catch(false),
    vaultIncludeChatSpaces: z.boolean().nullable().catch(false),
    vaultSharedDrivesOption: z.string().nullable().catch(null),
    vaultPeopleQuery: z.array(z.string()).nullable().catch(null),
    vaultLocationQuery: z.array(z.string()).nullable().catch(null),
    vaultMinusWords: z.array(z.string()).nullable().catch(null),
    vaultResponseStatus: z.array(z.string()).nullable().catch(null),
    destFolderName: z.string().nullable(),
    destFolderId: z.string().nullable(),
    transferAction: z
      .enum([
        'editor',
        'viewer',
        'removeOld',
        'removeAll',
        'commenter',
        'noAction',
      ])
      .nullable(),

    subject: z.string().nullable(),
    message: z.string().nullable(),

    preName: z.string().nullable(),
    postName: z.string().nullable(),
    addDate: z.boolean().nullable(),
    datePosition: z.enum(['prefix', 'suffix']).nullable(),
    mustAcknowledgeLogs: z.boolean(),
    useManagerEmail: z.boolean().nullable(),
    targetEmailType: z.array(z.string()).nullable(),
    customEmailType: z.array(z.string()).nullable(),
    useSharedDrive: z.boolean().nullable(),
    newPassword: z.string().nullable(),
    forceResetPassword: z.boolean().nullable(),
    staticPassword: z.boolean().nullable(),
    isSendPassword: z.boolean().nullable(),

    daysUntilExpiration: z.number().nullable(),

    emailSubject: z.string().nullable(),
    emailContent: z.string().nullable(),
    emailSendTo: z.array(z.string()).nullable(),
    emailCcTo: z.array(z.string()).nullable(),
    emailBccTo: z.array(z.string()).nullable(),
    sharingScope: z.enum(['all', 'external', 'internal']).nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      isVaultExportAction(data.actionType) &&
      !data.createMatterOnDecom &&
      (!data.matterId || data.matterId.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['matterId'],
        message: 'Please select or create a Vault Matter.',
      });
    }

    if (isVaultExportAction(data.actionType) && data.createMatterOnDecom) {
      if (
        !data.matterNameTemplateCustomText ||
        data.matterNameTemplateCustomText.trim() === ''
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['matterNameTemplateCustomText'],
          message: 'Custom text is required.',
        });
      }
      if (
        !data.matterNameTemplateProfileField ||
        data.matterNameTemplateProfileField.trim() === ''
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['matterNameTemplateProfileField'],
          message: 'Profile field is required.',
        });
      }
    }

    if (isVaultExportAction(data.actionType)) {
      const hasEndTime = data.vaultEndTime && data.vaultEndTime.trim() !== '';
      const hasStartTime =
        data.vaultStartTime && data.vaultStartTime.trim() !== '';

      if (hasEndTime && !hasStartTime) {
        ctx.addIssue({
          code: 'custom',
          path: ['vaultStartTime'],
          message: 'Start date is required when end date is specified.',
        });
      }

      if (data.vaultStartTime && isNaN(Date.parse(data.vaultStartTime))) {
        ctx.addIssue({
          code: 'custom',
          path: ['vaultStartTime'],
          message: 'Please enter a valid start date (MM/DD/YYYY).',
        });
      }
      if (data.vaultEndTime && isNaN(Date.parse(data.vaultEndTime))) {
        ctx.addIssue({
          code: 'custom',
          path: ['vaultEndTime'],
          message: 'Please enter a valid end date (MM/DD/YYYY).',
        });
      }
      if (data.vaultVersionDate && isNaN(Date.parse(data.vaultVersionDate))) {
        ctx.addIssue({
          code: 'custom',
          path: ['vaultVersionDate'],
          message: 'Please enter a valid version date (MM/DD/YYYY).',
        });
      }
    }

    if (
      isVaultExportAction(data.actionType) &&
      data.vaultStartTime &&
      !isNaN(Date.parse(data.vaultStartTime)) &&
      data.vaultEndTime &&
      !isNaN(Date.parse(data.vaultEndTime))
    ) {
      if (new Date(data.vaultEndTime) < new Date(data.vaultStartTime)) {
        ctx.addIssue({
          code: 'custom',
          path: ['vaultEndTime'],
          message: 'End date cannot be before start date.',
        });
      }
    }

    if (destUserRequiredActions.includes(data.actionType)) {
      if (
        (data.useManagerEmail === false || data.useManagerEmail == null) &&
        data.destUser
      ) {
        const ownerCount = data.destUser.filter(
          (u) => u.role === 'owner'
        ).length;
        if (ownerCount > 1) {
          ctx.addIssue({
            code: 'custom',
            path: ['destUser'],
            message:
              'Only one destination user with the role "owner" is allowed.',
          });
        }
        if (ownerCount < 1) {
          ctx.addIssue({
            code: 'custom',
            path: ['destUser'],
            message:
              'At least one destination user with the role "owner" is required.',
          });
        }
      }
    }
  });

// DecomActionListModel Schema
export const DecomActionListSchema = z.object({
  day: z.number(),
  actions: z.array(DecomActionSchema),
});

// Decom Form Data that have no fields in UI but part of Decom Model
export const DecomMetaSchema = z.object({
  createdBy: z.string().catch(''),
  createdTime: z.number().catch(0),
  lastUpdatedBy: z.string().catch(''),
  lastUpdatedTime: z.number().catch(0),
  id: z.string(),
  isActive: z.boolean().catch(true),
  userId: z.string().catch(''),
});

// Basic Information Step Schema  - includes all fields from this step
export const DecomBasicInfoSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'Policy Name is required.' }),
    org: z
      .array(z.string().min(1))
      .min(1, { message: 'Please provide at least one org unit.' }),
    notificationUser: z.string(),
    keepUnsuspended: z.boolean(),
    monitorSuspended: z.boolean(),
    sendSummary: z.boolean(),

    triggerType: z.enum(['suspension', 'move']),
    actImmediately: z.boolean(),
    validateUserIsStillInDecomOrg: z.boolean(),
    validateUserIsStillSuspended: z.boolean(),
  })
  .refine(
    (data) =>
      data.triggerType === 'suspension' || !data.validateUserIsStillSuspended,
    {
      message:
        "Pause Decom On Unsuspension is only valid if trigger type is 'suspension'.",
      path: ['validateUserIsStillSuspended'],
    }
  );

//  Action Configuration Step Schema  - includes all fields from this step
export const DecomActionConfigSchema = z.object({
  defaultNotifyUser: z.boolean().catch(false),
  defaultUserToNotify: z.array(z.string()).catch([]),
  defaultUseManagerEmail: z.boolean().nullable(),
  defaultDestUser: DestUserSchema.nullable(),
  customDays: z.array(z.number()).optional(),
});

//  First Run/Days Out Configuration Step Schema - includes all fields from this step
export const DecomActionRunConfigSchema = z.object({
  actions: z.record(z.string(), DecomActionListSchema),
});

export const DecomActionDaysSchema = z
  .object({
    day: z.number(),
  })
  .and(DecomActionSchema.partial());

//  DayActionStep: Use to validate each action per day in the run configuration
export const CreateDaySchema = (day: number) =>
  z.object({
    actions: z.object({
      [day]: z.object({
        actions: z.array(DecomActionSchema),
      }),
    }),
  });

export const DecomFormSchema = z.object({
  actionsData: z.array(DecomActionDaysSchema).optional(),
});

export const TransformedObjectSchema = z.record(z.number(), DecomActionSchema);

//  Decom Form Schema - merged all steps
//  You can use superRefine to add your validation
// Decom Model
export const DecomPolicyFormSchema = DecomMetaSchema.and(DecomBasicInfoSchema)
  .and(DecomActionConfigSchema)
  .and(DecomActionRunConfigSchema)
  .and(DecomFormSchema)
  .superRefine((data, ctx) => {
    //  Validation per decom actions
    (data.customDays ?? []).forEach((day) => {
      const actionsForDay = data.actions?.[day].actions ?? [];

      const hasValidAction = actionsForDay.some((a) => a.actionType);

      if (!hasValidAction) {
        ctx.addIssue({
          code: 'custom',
          message: `Please add at least one action.`,
          path: ['actions', day, 'actions'],
        });
      }

      actionsForDay.forEach((action, idx) => {
        const actionTypeValue =
          decomActions.find((a) => {
            return a.value === action.actionType;
          })?.label ?? '';
        if (
          action.actionType === 'transfercals' ||
          action.actionType === 'transferDocs' ||
          action.actionType === 'actSharedExternallyReportMyDrive' ||
          action.actionType === 'actSharedExternallyReportSharedDrive' ||
          action.actionType === 'actSharedPubliclyReportMyDrive' ||
          action.actionType === 'actSharedPubliclyReportSharedDrives' ||
          action.actionType === 'docsSharedExternallyReport' ||
          action.actionType === 'removeExternalShares' ||
          action.actionType === 'RemoveFromAllSharedDrive' ||
          action.actionType === 'addDelegate' ||
          action.actionType === 'setForwarding' ||
          action.actionType === 'ReassignNicknames' ||
          action.actionType === 'EmailDump' ||
          action.actionType === 'vaultGmailExport' ||
          action.actionType === 'vaultCalendarExport' ||
          action.actionType === 'vaultChatExport' ||
          action.actionType === 'vaultDriveExport' ||
          action.actionType === 'vaultGeminiExport' ||
          action.actionType === 'vaultGroupsExport'
        ) {
          if (action.useManagerEmail == null) {
            ctx.addIssue({
              path: ['actions', day, 'actions', idx, 'useManagerEmail'],
              code: 'custom',
              message: 'Please select a destination option.',
            });
          }
          if (
            action.useManagerEmail === false &&
            (!action.destUser || action.destUser.length === 0) &&
            !(action.actionType === 'transferDocs' && action.destFolderId)
          ) {
            ctx.addIssue({
              path: ['actions', day, 'actions', idx, 'destUser'],
              code: 'custom',
              message: `Please provide a valid email for the ${actionTypeValue} action.`,
            });
          }
          if (
            action.actionType === 'transferDocs' &&
            action.useManagerEmail === false
          ) {
            if (!action.destUser) {
              ctx.addIssue({
                path: ['actions', day, 'actions', idx, 'destUser'],
                code: 'custom',
                message: `Please provide a valid email for the ${actionTypeValue} action.`,
              });
            }

            if (action.useSharedDrive && !action.destFolderId) {
              ctx.addIssue({
                path: ['actions', day, 'actions', idx, 'destFolderId'],
                code: 'custom',
                message: 'Please select a Shared Drive.',
              });
            }
          }
        }

        if (action.actionType === 'delegateToRelation') {
          if (action.daysUntilExpiration == null) {
            ctx.addIssue({
              code: 'custom',
              path: ['actions', day, 'actions', idx, 'daysUntilExpiration'],
              message: 'Days until expiration is required.',
            });
          } else if (action.daysUntilExpiration < 1) {
            ctx.addIssue({
              code: 'custom',
              path: ['actions', day, 'actions', idx, 'daysUntilExpiration'],
              message: 'Days until expiration must be at least 1.',
            });
          } else if (action.daysUntilExpiration > 365) {
            ctx.addIssue({
              code: 'custom',
              path: ['actions', day, 'actions', idx, 'daysUntilExpiration'],
              message: 'Days until expiration cannot exceed 365.',
            });
          }
        }

        switch (action.actionType) {
          case 'ResetPassword': {
            if (
              action.staticPassword &&
              (!action.newPassword ||
                (action.newPassword && action.newPassword === ''))
            ) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'newPassword'],
                message: `Please provide a password for the ${actionTypeValue} action.`,
              });
            }
            break;
          }
          case 'delegateToRelation': {
            if (!action.destUser) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'destUser'],
                message: `Please provide a relation for the ${actionTypeValue} action.`,
              });
            }
            break;
          }
          case 'UserOrgChange': {
            if (!action.newOrgPath) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'newOrgPath'],
                message: `Please provide a org unit for the ${actionTypeValue} action.`,
              });
            }
            break;
          }
          case 'vacationResponder': {
            if (
              !action.subject ||
              (action.subject && action.subject.trim().length <= 0)
            ) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'subject'],
                message: `Subject is required.`,
              });
            }

            if (
              !action.message ||
              (action.subject && action.subject.trim().length <= 0)
            ) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'message'],
                message: `Message is required.`,
              });
            }
            break;
          }
          case 'sendEmail': {
            if (
              action.targetEmailType?.includes('custom') &&
              (!action.customEmailType || action.customEmailType.length === 0)
            ) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'customEmailType'],
                message: 'Please provide a custom email type.',
              });
            }
            // For a 'sendEmail' action, at least one recipient must be specified.
            // This can be the user's manager, a dynamic email type, or a manually entered email address.
            if (
              !action.useManagerEmail &&
              (!action.targetEmailType ||
                action.targetEmailType.length === 0) &&
              (!action.emailSendTo || action.emailSendTo.length <= 0)
            ) {
              ctx.addIssue({
                code: 'custom',
                path: ['actions', day, 'actions', idx, 'emailSendTo'],
                message: `Please provide a valid email for the ${actionTypeValue} to action.`,
              });
            }
            break;
          }
        }
      });
    });

    //  Validation for custom days
    if (data.customDays && data.customDays.length <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please add at least one timeline day before proceeding.',
        path: ['customDays'],
      });
    }

    // Validation for default destination user
    if (
      data.defaultUseManagerEmail === false &&
      !data.defaultDestUser?.userEmail?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please provide a default destination user.',
        path: ['defaultDestUser'],
      });
    }
  });

export type DecomActionValues = z.infer<typeof DecomActionSchema>;
export type DecomActionDaysValues = z.infer<typeof DecomActionDaysSchema>;
export type DecomActionListValues = z.infer<typeof DecomActionListSchema>;
